import { Bar, Chart, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineController,
  LineElement,
  SubTitle,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import CommonUtil from 'utils/common.util';

ChartJS.register(
  BarElement,
  LineController,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  Title,
  SubTitle,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const font = { size: 22 };

/** 프로젝트 차트 */
export const ProjectChart = ({ statsData }) => {
  const data = {
    labels: statsData.map((item) => `${item.year}년`),
    datasets: [
      {
        type: 'line',
        label: '수주금액(억원)',
        data: statsData.map((item) => parseInt(item.projectBudgetAmount) / 100000000),
        borderColor: '#b4b4b4',
        backgroundColor: '#b4b4b4',
        pointBackgroundColor: '#4B4B4B',
        yAxisID: 'y1',
        datalabels: {
          align: 'top',
          anchor: 'end',
          backgroundColor: '#4B4B4B',
          borderRadius: 50,
          color: '#f5f4f1',
          font: {
            weight: 'bold',
            size: 10,
          },
          padding: 6,
          display: true,
          formatter: (value) => {
            return parseFloat(value.toFixed(3));
          },
        },
      },
      {
        type: 'bar',
        label: '프로젝트(건)',
        data: statsData.map((item) => item.projectCount),
        backgroundColor: '#4472c4',
        stack: 'stack 0',
        barThickness: 80,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: '보육기업(개사)',
        data: statsData.map((item) => item.companyCount),
        backgroundColor: '#ff9334',
        stack: 'stack 0',
        barThickness: 80,
        yAxisID: 'y',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      title: {
        display: true,
        text: '프로젝트 운영 현황',
        font,
      },
      datalabels: {
        color: '#f5f4f1',
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grace: '10%',
        title: {
          display: true,
          text: '건수 / 개사',
        },
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        grace: '10%',
        title: {
          display: true,
          text: '수주금액 (억원)',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return <Chart data={data} options={options} height={400} />;
};

/** 보육 기업 차트 */
export const IncubatedCompanyChart = ({ statsData }) => {
  const isAllZero = statsData.companyCount == 0 && statsData.membershipCompanyCount == 0;

  const data = {
    labels: ['보육기업', '멤버십기업'],
    datasets: [
      {
        data: [statsData.companyCount, statsData.membershipCompanyCount],
        backgroundColor: ['#4472c4', '#ff9334'],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: `보육 기업 (총 기업 수: ${statsData.companyCount + statsData.membershipCompanyCount}개사)`,
        font,
      },
      datalabels: {
        color: '#f5f4f1',
      },
    },
  };

  return isAllZero ? (
    <div className="chart-no-data">
      <p>보육 기업 (총 기업 수: 0)</p>
      <div>데이터 없음</div>
    </div>
  ) : (
    <Pie data={data} options={options} height={400} />
  );
};

/** 매출 차트 */
export const SalesChart = ({ statsData }) => {
  const data = {
    labels: statsData.map((item) => `${item.year}년`),
    datasets: [
      {
        label: '국내',
        data: statsData.map((item) => parseInt(item.domesticSalesAmount) / 10000000),
        backgroundColor: '#4472c4',
        stack: 'stack 0',
        barThickness: 60,
      },
      {
        label: '해외',
        data: statsData.map((item) => parseInt(item.overseasSalesAmount) / 10000000),
        backgroundColor: '	#ff9334',
        stack: 'stack 0',
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: '매출',
        font,
      },
      datalabels: {
        color: '#f5f4f1',
        formatter: (value) => {
          return parseFloat(value.toFixed(3));
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grace: '10%',
        title: {
          display: true,
          text: '단위: 천만원',
        },
      },
    },
  };

  return <Bar data={data} options={options} height={400} />;
};

/** 고용 차트 */
export const EmploymentChart = ({ statsData }) => {
  const data = {
    labels: statsData.map((item) => `${item.year}년`),
    datasets: [
      {
        label: '정규',
        data: statsData.map((item) => item.permanentEmployeesCount),
        backgroundColor: '#4472c4',
        stack: 'stack 0',
        barThickness: 60,
      },
      {
        label: '비정규',
        data: statsData.map((item) => item.temporaryEmployeesCount),
        backgroundColor: '#ff9334',
        stack: 'stack 0',
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: '고용',
        font,
      },
      datalabels: {
        color: '#f5f4f1',
        formatter: (value) => {
          return CommonUtil.tsCommaFormat(value);
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grace: '10%',
        title: {
          display: true,
          text: '단위: 명',
        },
      },
    },
  };

  return <Bar data={data} options={options} height={400} />;
};

/** 투자 차트 */
export const InvestmentChart = ({ statsData }) => {
  const data = {
    labels: [statsData.year],
    datasets: [
      {
        data: [parseInt(statsData.investmentAmount) / 10000000],
        backgroundColor: '#4472c4',
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: '투자',
        font,
      },
      datalabels: {
        color: '#f5f4f1',
        formatter: (value) => {
          return parseFloat(value.toFixed(3));
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        grace: '10%',
        title: {
          display: true,
          text: '단위: 천만원',
        },
      },
    },
  };

  return <Bar data={data} options={options} height={400} />;
};

/** 특허 차트 */
export const PatentChart = ({ statsData }) => {
  const isAllZero =
    statsData.patentAppliedCount == 0 &&
    statsData.patentRegisteredCount == 0 &&
    statsData.trademarkAppliedCount == 0 &&
    statsData.trademarkRegisteredCount == 0;

  const data = {
    labels: ['특허출원', '특허등록', '상표출원', '상표등록'],
    datasets: [
      {
        data: [
          statsData.patentAppliedCount,
          statsData.patentRegisteredCount,
          statsData.trademarkAppliedCount,
          statsData.trademarkRegisteredCount,
        ],
        backgroundColor: ['#4472c4', '#ff9334', '#cbcbcb', '#ffc000'],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: '지식재산권',
        font,
      },
      datalabels: {
        color: '#f5f4f1',
      },
    },
  };

  return isAllZero ? (
    <div className="chart-no-data">
      <p>지식재산권</p>
      <div>데이터 없음</div>
    </div>
  ) : (
    <Pie data={data} options={options} height={400} />
  );
};

/** 벤처기업 차트 */
export const VentureChart = ({ statsData }) => {
  const isAllZero = statsData.totalCompanyCount == 0 && statsData.ventureCompanyCount == 0;

  const data = {
    labels: ['기업수', '벤처기업'],
    datasets: [
      {
        data: [statsData.totalCompanyCount, statsData.ventureCompanyCount],
        backgroundColor: ['#4472c4', '#ff9334'],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: '벤처기업',
        font,
      },
      datalabels: {
        color: '#f5f4f1',
      },
    },
  };

  return isAllZero ? (
    <div className="chart-no-data">
      <p>벤처기업</p>
      <div>데이터 없음</div>
    </div>
  ) : (
    <Pie data={data} options={options} height={400} />
  );
};
